import { useState } from "react";
import { Link } from "react-router-dom";
import { Bus, MapPin, Zap } from "lucide-react";

import { useMyApplications, useWithdraw } from "@/features/applications/model/useApplications";
import type { ApplicationStatus } from "@/features/applications/types";
import { CATEGORY_LABELS, ROLE_LABELS } from "@/features/shifts/types";
import { formatTimeRange12h } from "@/shared/lib/time";
import { Badge, type BadgeProps } from "@/shared/ui";

const STATUS_META: Record<ApplicationStatus, { label: string; variant: BadgeProps["variant"] }> = {
  PENDING: { label: "Pending", variant: "warning" },
  INTERESTED: { label: "Shortlisted", variant: "success" },
  PASSED: { label: "Passed", variant: "neutral" },
  CONFIRMED: { label: "Confirmed", variant: "success" },
  WITHDRAWN: { label: "Withdrawn", variant: "neutral" },
};

function formatShiftDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatDateRange(start: string, end: string | null): string {
  if (!end || end === start) return formatShiftDate(start);
  return `${formatShiftDate(start)} – ${formatShiftDate(end)}`;
}

function computeHours(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let minutes = eh * 60 + em - (sh * 60 + sm);
  if (minutes <= 0) minutes += 24 * 60;
  return Math.round((minutes / 60) * 100) / 100;
}

function payNumber(rate: string): number {
  const n = parseFloat(rate);
  return isNaN(n) ? 0 : n;
}

export function MyApplications() {
  const { data, isLoading, error } = useMyApplications();
  const withdraw = useWithdraw();
  // Track in-flight withdrawals per row so the loading state stays scoped to
  // the clicked button instead of every Withdraw button on the page.
  const [pendingWithdrawIds, setPendingWithdrawIds] = useState<Set<string>>(new Set());
  const handleWithdraw = (id: string) => {
    setPendingWithdrawIds((s) => new Set(s).add(id));
    withdraw.mutate(id, {
      onSettled: () =>
        setPendingWithdrawIds((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        }),
    });
  };

  if (isLoading) return <div className="text-sm text-copy-muted">Loading…</div>;
  if (error) return <div className="text-sm text-destructive">Could not load applications.</div>;
  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface p-10 text-center shadow-card">
        <p className="text-sm font-medium text-copy">No applications yet</p>
        <p className="mt-1 text-sm text-copy-muted">
          Browse open shifts and apply — they'll show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data.map((app) => {
        const meta = STATUS_META[app.status];
        const hours = computeHours(app.shift_start_time, app.shift_end_time);
        const rate = payNumber(app.pay_rate);
        const estimated = rate > 0 && hours > 0 ? (rate * hours).toFixed(2) : null;
        const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          app.venue_address,
        )}`;

        return (
          <div
            key={app.id}
            className="rounded-lg border border-border bg-surface p-4 shadow-card transition-colors hover:border-border-strong"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/worker/shifts/${app.shift_id}`}
                    className="truncate font-medium text-primary hover:underline"
                    title={`${app.shift_event_name ?? ROLE_LABELS[app.shift_role]} at ${app.venue_name}`}
                  >
                    {app.shift_event_name ?? ROLE_LABELS[app.shift_role]} · {app.venue_name}
                  </Link>
                  {app.shift_event_name && (
                    <Badge variant="neutral">{ROLE_LABELS[app.shift_role]}</Badge>
                  )}
                  {app.is_urgent && (
                    <Badge variant="destructive" className="gap-1">
                      <Zap className="h-3 w-3" /> Urgent
                    </Badge>
                  )}
                  {app.transportation_offered && (
                    <Badge variant="neutral" className="gap-1">
                      <Bus className="h-3 w-3" /> Transit
                    </Badge>
                  )}
                  {app.shift_category && (
                    <Badge variant="neutral">
                      {CATEGORY_LABELS[app.shift_category]}
                    </Badge>
                  )}
                </div>

                <p className="text-sm text-copy">
                  {formatDateRange(app.shift_date, app.shift_end_date)} ·{" "}
                  {formatTimeRange12h(app.shift_start_time, app.shift_end_time)}{" "}
                  <span className="text-copy-muted">({hours} hrs)</span>
                </p>

                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-1.5 text-xs text-copy-muted hover:text-primary hover:underline"
                  title="Open in Google Maps"
                >
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="break-words">{app.venue_address}</span>
                </a>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-copy-muted">
                  <span className="text-copy">
                    <span className="font-medium tabular-nums">${rate.toFixed(2)}/hr</span>
                    {estimated && (
                      <span className="text-copy-muted"> · est. ${estimated}</span>
                    )}
                  </span>
                  <span className="text-copy-subtle">
                    Applied {new Date(app.applied_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-2">
                <Badge variant={meta.variant} dot>
                  {meta.label}
                </Badge>
                {app.status === "PENDING" && (
                  <button
                    onClick={() => handleWithdraw(app.id)}
                    disabled={pendingWithdrawIds.has(app.id)}
                    className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                  >
                    {pendingWithdrawIds.has(app.id) ? "Withdrawing…" : "Withdraw"}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
